import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, PackagePlus, ShoppingBag } from 'lucide-react';
import * as Icons from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { SharedItem } from '../../types/shared-items';
import ItemModal from './ItemModal';

const ItemList = () => {
  const [items, setItems] = useState<SharedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SharedItem | null>(null);
  const [activeType, setActiveType] = useState<'include' | 'bring'>('include');

  useEffect(() => {
    fetchItems();
  }, [activeType]);

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from('shared_items')
        .select('*')
        .eq('type', activeType)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('Error fetching items:', err);
      setError('Failed to fetch items');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const { error } = await supabase
        .from('shared_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setItems(items.filter(item => item.id !== id));
    } catch (err) {
      console.error('Error deleting item:', err);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="space-y-6">
      {/* Type Selector */}
      <div className="flex gap-4">
        <button
          onClick={() => setActiveType('include')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            activeType === 'include'
              ? 'bg-[#E85C0D] text-white'
              : 'bg-white/10 text-white hover:bg-white/20'
          }`}
        >
          <PackagePlus className="w-4 h-4" />
          What's Included
        </button>
        <button
          onClick={() => setActiveType('bring')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            activeType === 'bring'
              ? 'bg-[#E85C0D] text-white'
              : 'bg-white/10 text-white hover:bg-white/20'
          }`}
        >
          <ShoppingBag className="w-4 h-4" />
          What to Bring
        </button>
      </div>

      {/* Add Item Button */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            setEditingItem(null);
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Item
        </button>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((item) => (
          <div
            key={item.id}
            className="p-4 rounded-lg border-2 border-white/10 hover:border-white/20 transition-all"
          >
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                {item.icon && Icons[item.icon as keyof typeof Icons] && (
                  React.createElement(Icons[item.icon as keyof typeof Icons], {
                    className: "w-6 h-6"
                  })
                )}
                <div>
                  <h4 className="font-medium">{item.title}</h4>
                  <p className="text-sm text-white/70">{item.description}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setEditingItem(item);
                    setIsModalOpen(true);
                  }}
                  className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="p-1 hover:bg-white/10 rounded-lg transition-colors text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ItemModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingItem(null);
        }}
        item={editingItem}
        type={activeType}
        onSave={fetchItems}
      />
    </div>
  );
};

export default ItemList;